import { z } from 'zod'

export const emailSchema = z.string().email()

export const isValidChipEmail = (email: string): boolean => emailSchema.safeParse(email).success

const ANGLE_ADDR = /<[^<>\s]+@[^<>\s]+>/
const ALL_ANGLE_ADDRS = /<([^<>\s]+@[^<>\s]+)>/g
const QUOTED_DISPLAY_NAME = /"[^"]*"/g

export const hasAngleAddrMailbox = (input: string): boolean => ANGLE_ADDR.test(input)

export const normalizeEmailInput = (input: string): string => {
  const match = input.match(ANGLE_ADDR)
  const candidate = match ? match[0].slice(1, -1) : input
  return candidate.trim().toLowerCase()
}

export interface ParsedEmails {
  valid: string[]
  invalid: string[]
}

export const parseEmailsWithValidation = (input: string): ParsedEmails => {
  const hadAngleAddrs = hasAngleAddrMailbox(input)
  // RFC 5322-style name-addr mailboxes ("Doe, Jane" <jane@x.com>) as pasted from
  // email clients' To/Cc fields: keep each angle-bracket address in place (so list
  // order survives) and drop quoted display names entirely — the commas inside them
  // must never reach the separator split. Unquoted display names fall through to the
  // split and are discarded below as @-less fragments.
  const flattened = hadAngleAddrs
    ? input.replace(ALL_ANGLE_ADDRS, (_match, addr: string) => `,${addr},`).replace(QUOTED_DISPLAY_NAME, ',')
    : input

  const entries = flattened
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0)

  const result: ParsedEmails = { valid: [], invalid: [] }
  for (const entry of entries) {
    if (emailSchema.safeParse(entry).success) {
      result.valid.push(entry)
    } else if (!hadAngleAddrs || entry.includes('@')) {
      // With angle-bracket mailboxes present, tokens without an @ are leftover
      // display-name words (e.g. the "Doe" of an unquoted "Doe, Jane <...>"),
      // not typos worth surfacing.
      result.invalid.push(entry)
    }
  }
  return result
}
